import { Link } from 'react-router-dom';
import { useEffect } from 'react';

function UXUIDesigner() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const position = {
    title: 'UX/UI Designer',
    department: 'Design',
    location: 'Worldwide (Remote)',
    type: 'Full-Time',
    salaryRange: '$700-$1,200 USD',
    // ... rest of the position object
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <Link
            to="/careers"
            className="text-[#0049ac] hover:text-[#0049ac]/90 flex items-center"
          >
            ← Back to Careers
          </Link>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main content */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-xl overflow-hidden">
              <div className="p-8">
                <div className="mb-8">
                  <h1 className="text-3xl font-bold text-gray-900 mb-4">{position.title}</h1>
                  <div className="flex items-center gap-2 mb-6">
                    <p className="text-gray-500">{position.department} • {position.location} • {position.type}</p>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      {position.salaryRange}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-3">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                      Full Remote
                    </span>
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800">
                      SellAbroad
                    </span>
                  </div>
                </div>

                <div className="prose max-w-none">
                  <div className="mb-8">
                    <h2 className="text-xl font-semibold mb-4">About SellAbroad</h2>
                    <p>
                      SellAbroad empowers international e-commerce brands to expand into the MENA market by streamlining cross-border logistics, integrating local payment methods, and leveraging AI sales tools. We build robust, scalable, and secure e-commerce applications that drive global success.
                    </p>
                  </div>

                  <div className="mb-8">
                    <h2 className="text-xl font-semibold mb-4">Job Description</h2>
                    <p>
                      We are seeking a creative and efficient UX/UI Designer to join our team. The ideal candidate will excel at rapidly building screens using Figma and have hands-on experience designing e-commerce application interfaces. You will collaborate with cross-functional teams to translate business needs into intuitive and engaging designs that enhance the user experience.
                    </p>
                  </div>

                  <div className="mb-8">
                    <h2 className="text-xl font-semibold mb-4">Key Responsibilities</h2>
                    <ul className="list-disc pl-5 space-y-6">
                      <li>
                        <h3 className="font-medium mb-2 inline-block">Design & Prototyping</h3>
                        <ul className="list-disc pl-5 mt-2">
                          <li>Quickly create high-fidelity designs and prototypes using Figma.</li>
                          <li>Develop intuitive, user-centric interfaces for our e-commerce applications.</li>
                        </ul>
                      </li>

                      <li>
                        <h3 className="font-medium mb-2 inline-block">Collaboration</h3>
                        <ul className="list-disc pl-5 mt-2">
                          <li>Collaborate with developers and product teams to ensure designs align with business objectives and technical requirements.</li>
                        </ul>
                      </li>

                      <li>
                        <h3 className="font-medium mb-2 inline-block">Design System Management</h3>
                        <ul className="list-disc pl-5 mt-2">
                          <li>Maintain design consistency and enforce brand guidelines across all digital touchpoints.</li>
                        </ul>
                      </li>

                      <li>
                        <h3 className="font-medium mb-2 inline-block">User Experience</h3>
                        <ul className="list-disc pl-5 mt-2">
                          <li>Iterate on designs based on user feedback and usability testing.</li>
                        </ul>
                      </li>

                      <li>
                        <h3 className="font-medium mb-2 inline-block">Professional Development</h3>
                        <ul className="list-disc pl-5 mt-2">
                          <li>Stay current with industry trends and best practices in UX/UI design for e-commerce platforms.</li>
                        </ul>
                      </li>
                    </ul>
                  </div>

                  <div className="mb-8">
                    <h2 className="text-xl font-semibold mb-4">Qualifications</h2>
                    <ul className="list-disc pl-5">
                      <li>Minimum 3 years of experience in UX/UI design, with a focus on e-commerce application interfaces.</li>
                      <li>Proficiency in Figma for rapid screen building and prototyping.</li>
                      <li>Strong portfolio showcasing user-centric design solutions for web or mobile applications.</li>
                      <li>Excellent understanding of design principles, responsive design, and interaction design.</li>
                      <li>Ability to work efficiently in a fast-paced, remote environment.</li>
                      <li>Strong communication and collaboration skills.</li>
                      <li>Bachelor's degree in Design, Human-Computer Interaction, or a related field, or equivalent practical experience.</li>
                    </ul>
                  </div>

                  <div className="mb-8">
                    <h2 className="text-xl font-semibold mb-4">What We Offer</h2>
                    <ul className="list-disc pl-5">
                      <li>Fully remote work environment with the flexibility to work from anywhere.</li>
                      <li>A dynamic, innovative team driving e-commerce innovation in the MENA market.</li>
                      <li>A collaborative and growth-focused work environment with competitive salary and benefits.</li>
                      <li>Opportunities for professional development and career advancement.</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Floating apply card */}
          <div className="lg:col-span-1">
            <div className="sticky top-24">
              <div className="bg-white rounded-lg shadow-xl p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Apply for this Position</h2>
                <p className="text-gray-600 mb-6">
                  Join our team and help transform e-commerce in the MENA region.
                </p>
                <Link
                  to="/careers/ux-ui-designer/apply"
                  className="w-full flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-[#0049ac] hover:bg-[#0049ac]/90 transition-all duration-200"
                >
                  Apply Now
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default UXUIDesigner; 